import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch, Select, Tag
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, ShoppingOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled, WarningOutlined,
    FilterOutlined, ClearOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManageInventoryItems = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    // Top filter bar state (server-side search + category filter)
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState(undefined);
    const [applied, setApplied] = useState({});

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchCategories = useCallback(async () => {
        try {
            // fetch all active categories for dropdown (using page=1 limit=100 or a high number)
            const res = await api.get('/inventory/category/list.php?page=1&limit=100&active_only=1');
            setCategories(res.data.dataset);
        } catch {
            message.error("Failed to load inventory categories.");
        }
    }, []);

    const fetchUoms = useCallback(async () => {
        try {
            const res = await api.get('/masterdropdown/listof_activeuoms.php');
            setUoms(res.data.dataset || []);
        } catch {
            message.error("Failed to load units of measurement.");
        }
    }, []);

    const fetchItems = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/inventory/items/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                    ic_id: filters.ic_id || 0,
                },
            });
            setItems(res.data.dataset.map(item => ({
                invId: item.invi_id,
                invCode: item.inv_code,
                itemName: item.item_name,
                categoryName: item.category_name || '',
                icId: item.ic_id,
                uomId: item.uom_id || null,
                uomName: item.uom_name || '',
                uomSymbol: item.uom_symbol || '',
                invDesc: item.inv_desc,
                costPrice: item.cost_price,
                availableQty: item.available_qty,
                reorderQty: item.reorder_qty,
                isActive: item.is_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch inventory items.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems(1, 10, {});
        fetchCategories();
        fetchUoms();
    }, [fetchItems, fetchCategories, fetchUoms]);

    const handleTableChange = (pg) => fetchItems(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            ic_id: filterCat || 0,
        };
        setApplied(filters);
        fetchItems(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setFilterCat(undefined);
        setApplied({});
        fetchItems(1, pagination.pageSize, {});
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/inventory/items/add.php', {
                inv_code: values.invCode,
                item_name: values.itemName,
                ic_id: values.icId,
                uom_id: values.uomId || 0,
                inv_desc: values.invDesc,
                cost_price: values.costPrice,
                available_qty: values.availableQty || 0,
                reorder_qty: values.reorderQty || 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Item added successfully.");
                setIsAddModalVisible(false);
                fetchItems(pagination.current, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add item.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            invCode: record.invCode,
            itemName: record.itemName,
            icId: record.icId,
            uomId: record.uomId || undefined,
            invDesc: record.invDesc,
            costPrice: record.costPrice,
            reorderQty: record.reorderQty,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/inventory/items/edit.php', {
                invi_id: currentItem.invId,
                inv_code: values.invCode,
                item_name: values.itemName,
                ic_id: values.icId,
                uom_id: values.uomId || 0,
                inv_desc: values.invDesc,
                cost_price: values.costPrice,
                reorder_qty: values.reorderQty || 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Item updated successfully.");
                setIsModalVisible(false);
                fetchItems(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update item.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/inventory/items/toggle.php', {
                invi_id: record.invId,
                is_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchItems(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const columns = [
        {
            title: 'Code', dataIndex: 'invCode', key: 'invCode', width: 100,
            ...getColumnSearchProps('invCode'),
        },
        {
            title: 'Item Name', dataIndex: 'itemName', key: 'itemName',
            sorter: (a, b) => a.itemName.localeCompare(b.itemName),
            ...getColumnSearchProps('itemName'),
        },
        {
            title: 'Category', dataIndex: 'categoryName', key: 'categoryName',
            sorter: (a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''),
            ...getColumnSearchProps('categoryName'),
            render: (val) => val || <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Description', dataIndex: 'invDesc', key: 'invDesc',
            ...getColumnSearchProps('invDesc'),
        },
        {
            title: 'UOM', dataIndex: 'uomName', key: 'uomName', width: 110,
            align: 'center',
            render: (val, record) => record.uomName
                ? (record.uomSymbol ? `${val} (${record.uomSymbol})` : val)
                : <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Cost Price', dataIndex: 'costPrice', key: 'costPrice',
            align: 'right',
            render: (val) => `LKR ${parseFloat(val).toFixed(2)}`,
        },
        {
            title: 'Available Qty', dataIndex: 'availableQty', key: 'availableQty',
            align: 'center',
            sorter: (a, b) => a.availableQty - b.availableQty,
            render: (qty, record) => {
                const needsReorder = qty <= record.reorderQty;
                return (
                    <Space>
                        <span style={{ fontWeight: needsReorder ? 'bold' : 'normal', color: needsReorder ? '#f5222d' : 'inherit' }}>
                            {qty}{record.uomName ? ` ${record.uomName}` : ''}
                        </span>
                        {needsReorder && (
                            <Tooltip title={`Low Stock Alert! Reorder limit is ${record.reorderQty}${record.uomName ? ` ${record.uomName}` : ''}`}>
                                <WarningOutlined style={{ color: '#f5222d' }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Reorder Level', dataIndex: 'reorderQty', key: 'reorderQty',
            align: 'center',
        },
        {
            title: 'Status', dataIndex: 'isActive', key: 'isActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Item">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.isActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.isActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { title: 'Inventory' },
                                    { title: 'Items' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <ShoppingOutlined /> Inventory Items
                        </h1>
                        <p className="fluid-page-subtitle">Track and configure details for stock items</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add New Item
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Code, name, description"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Category:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterCat}
                                    onChange={setFilterCat}
                                    placeholder="All Categories"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((cat) => <Option key={cat.ic_id} value={cat.ic_id}>{cat.ic_name}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={items}
                            rowKey="invId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 1000 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add New Item"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={6}>
                            <Form.Item label="Code" name="invCode" rules={[{ required: true, message: 'Item code required' }]}>
                                <Input placeholder="ITM-001" maxLength={20} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Item Name" name="itemName" rules={[{ required: true, message: 'Item name required' }]}>
                                <Input placeholder="NPK Fertilizer" maxLength={150} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Category" name="icId" rules={[{ required: true, message: 'Category required' }]}>
                                <Select allowClear placeholder="Select Category">
                                    {categories.map(cat => (
                                        <Option key={cat.ic_id} value={cat.ic_id}>{cat.ic_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Unit of Measurement" name="uomId">
                                <Select placeholder="Select UOM" allowClear>
                                    {uoms.map(uom => (
                                        <Option key={uom.value} value={Number(uom.value)}>
                                            {uom.label}{uom.uom_symbol ? ` (${uom.uom_symbol})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Cost Price (LKR)" name="costPrice" rules={[{ required: true, message: 'Cost price required' }]}>
                                <Input type="number" step="0.01" min="0" placeholder="1500.00" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Initial Qty" name="availableQty" rules={[{ required: true, message: 'Initial quantity required' }]}>
                                <Input type="number" min="0" placeholder="10" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={16}>
                            <Form.Item label="Description" name="invDesc" rules={[{ required: true, message: 'Description required' }]}>
                                <Input placeholder="NPK Fertilizer 50kg bag" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Reorder Qty Level" name="reorderQty" rules={[{ required: true, message: 'Reorder quantity level required' }]}>
                                <Input type="number" min="0" placeholder="5" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Item</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Item Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={700}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col span={8}>
                            <Form.Item label="Code" name="invCode" rules={[{ required: true, message: 'Item code is required' }]}>
                                <Input maxLength={20} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Item Name" name="itemName" rules={[{ required: true, message: 'Item name is required' }]}>
                                <Input maxLength={150} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Category" name="icId" rules={[{ required: true, message: 'Category is required' }]}>
                                <Select allowClear>
                                    {categories.map(cat => (
                                        <Option key={cat.ic_id} value={cat.ic_id}>{cat.ic_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Description" name="invDesc" rules={[{ required: true, message: 'Description is required' }]}>
                        <Input />
                    </Form.Item>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Form.Item label="Unit of Measurement" name="uomId">
                                <Select placeholder="Select UOM" allowClear>
                                    {uoms.map(uom => (
                                        <Option key={uom.value} value={Number(uom.value)}>
                                            {uom.label}{uom.uom_symbol ? ` (${uom.uom_symbol})` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Cost Price" name="costPrice" rules={[{ required: true, message: 'Cost price is required' }]}>
                                <Input type="number" step="0.01" min="0" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Form.Item label="Reorder Qty Level" name="reorderQty" rules={[{ required: true, message: 'Reorder level is required' }]}>
                                <Input type="number" min="0" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageInventoryItems;
